// LanguageSelector — flag dropdown used in Login, Settings and (compact
// variant) the top-right of the Game view. Persists the choice to
// localStorage AND syncs to the server through PATCH
// /api/character/preferences so it survives across devices.
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { Globe, ChevronDown } from "lucide-react";

export default function LanguageSelector({ variant = "compact", onChanged }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[1];

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = async (code) => {
    setOpen(false);
    if (code === i18n.language) return;
    i18n.changeLanguage(code);
    try { localStorage.setItem("gw_language", code); } catch (e) { /* noop */ }
    // Best-effort server sync — silent failure keeps client-only UX working.
    api.patchPreferences({ language: code }).catch(() => {});
    onChanged?.(code);
  };

  if (variant === "full") {
    // Grid variant used inside Settings / Login where we have room.
    return (
      <div className="space-y-2" data-testid="language-selector-full">
        <div className="font-heading text-[10px] tracking-[0.25em] uppercase text-[#E5C34A] flex items-center gap-2">
          <Globe className="w-3 h-3" strokeWidth={1.5} /> {t("settings.language")}
        </div>
        <div className="grid grid-cols-5 gap-2">
          {SUPPORTED_LANGUAGES.map(lang => (
            <button key={lang.code} onClick={() => pick(lang.code)}
              data-testid={`lang-option-${lang.code}`}
              className={`flex flex-col items-center gap-1 px-2 py-2 border font-mono text-[9px] uppercase tracking-widest transition-colors
                ${i18n.language === lang.code
                  ? "border-[#E55A00] text-[#E55A00] bg-[#E55A00]/10"
                  : "border-[#262626] text-[#B0ADA5] hover:border-[#E5C34A] hover:text-[#E5C34A]"}`}>
              <span className="text-xl leading-none">{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
        <div className="font-mono text-[9px] text-[#666]">{t("settings.languageHint")}</div>
      </div>
    );
  }

  // Compact dropdown (top-right of Game view / Login corner).
  return (
    <div className="relative inline-block" ref={ref} data-testid="language-selector-compact">
      <button onClick={() => setOpen(o => !o)}
        data-testid="lang-button"
        className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#262626] hover:border-[#E5C34A] font-mono text-[10px] uppercase tracking-widest text-[#B0ADA5]">
        <span className="text-base leading-none">{current.flag}</span>
        <span>{current.code.toUpperCase()}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] panel p-1 shadow-xl">
          {SUPPORTED_LANGUAGES.map(lang => (
            <button key={lang.code} onClick={() => pick(lang.code)}
              data-testid={`lang-dropdown-${lang.code}`}
              className={`w-full flex items-center gap-2 px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors
                ${i18n.language === lang.code
                  ? "text-[#E55A00] bg-[#E55A00]/10"
                  : "text-[#B0ADA5] hover:text-[#E5C34A] hover:bg-[#1a1a1a]"}`}>
              <span className="text-base leading-none">{lang.flag}</span>
              <span className="flex-1 text-left">{lang.label}</span>
              <span className="text-[#666] text-[9px]">{lang.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
